import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import toast from "react-hot-toast";

import {
  advertisementsApi,
} from "../api/advertisementsApi";

import type {
  Advertisement,
}  from "../types/advertisement.types";

import {
  useAuth,
} from "../contexts/AuthContext";

function formatDate(
  value?: string | null,
) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      dateStyle:
        "medium",
      timeStyle:
        "short",
    },
  ).format(
    new Date(
      value,
    ),
  );
}

const AdvertisementDetails:
React.FC = () => {
  const {
    id,
  } =
    useParams<{
      id: string;
    }>();

  const navigate =
    useNavigate();

  const {
    hasPermission,
  } = useAuth();

  const [
    advertisement,
    setAdvertisement,
  ] =
    useState<Advertisement | null>(
      null,
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const ctr =
    useMemo(
      () => {
        if (
          !advertisement ||
          advertisement
            .analytics
            .impressions ===
            0
        ) {
          return 0;
        }

        return (
          advertisement
            .analytics.clicks /
          advertisement
            .analytics
            .impressions *
          100
        );
      },
      [
        advertisement,
      ],
    );

  useEffect(() => {
    if (!id) {
      return;
    }

    const load =
      async () => {
        try {
          setLoading(
            true,
          );

          setAdvertisement(
            await advertisementsApi.getById(
              id,
            ),
          );
        } catch (
          error
        ) {
          console.error(
            error,
          );

          toast.error(
            "تعذر تحميل الإعلان",
          );
        } finally {
          setLoading(
            false,
          );
        }
      };

    load();
  }, [
    id,
  ]);

  if (
    !hasPermission(
      "advertisements.view",
    )
  ) {
    return (
      <Box
        p={4}
        dir="rtl"
      >
        لا تملك صلاحية عرض
        الإعلان.
      </Box>
    );
  }

  if (
    loading
  ) {
    return (
      <Box
        sx={{
          minHeight:
            350,
          display:
            "grid",
          placeItems:
            "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (
    !advertisement
  ) {
    return (
      <Box
        p={4}
        dir="rtl"
      >
        الإعلان غير موجود.
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          md: 3,
        },
        background:
          "#f8fafc",
      }}
      dir="rtl"
    >
      <Stack
        direction={{
          xs:
            "column",
          md:
            "row",
        }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography
            variant="h4"
            fontWeight={900}
          >
            {
              advertisement.name
            }
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            mt={1}
          >
            <Chip
              label={
                advertisement.status
              }
            />

            <Chip
              label={
                advertisement.displayType
              }
              variant="outlined"
            />
          </Stack>
        </Box>

        <Stack
          direction="row"
          spacing={1}
        >
          <Button
            variant="outlined"
            onClick={() =>
              navigate(
                "/advertisements",
              )
            }
          >
            رجوع
          </Button>

          {hasPermission(
            "advertisements.update",
          ) && (
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  `/advertisements/${advertisement.id}/edit`,
                )
              }
            >
              تعديل
            </Button>
          )}
        </Stack>
      </Stack>

     {advertisement.media.imageUrl ? (
  <div className="advertisement-details-media">
    <div className="advertisement-details-main-image">
      <img
        src={
          advertisement.media.imageUrl
        }
        alt={
          advertisement.name
        }
      />

      <span className="advertisement-details-image-badge">
        الصورة الرئيسية
      </span>
    </div>

    {advertisement.media.mobileImageUrl && (
      <div className="advertisement-details-mobile-image">
        <img
          src={
            advertisement
              .media
              .mobileImageUrl
          }
          alt={`${advertisement.name} - mobile`}
        />

        <span className="advertisement-details-image-badge">
          صورة الهاتف
        </span>
      </div>
    )}
  </div>
) : (
  <div className="advertisement-details-no-image">
    لا توجد صورة رئيسية لهذا الإعلان حتى الآن.
    يجب رفع صورة قبل نشر الإعلان.
  </div>
)}

      <Grid
        container
        spacing={2}
        mb={3}
      >
        {[
          [
            "مرات الظهور",
            advertisement
              .analytics
              .impressions,
          ],
          [
            "النقرات",
            advertisement
              .analytics
              .clicks,
          ],
          [
            "المستخدمون الفريدون",
            advertisement
              .analytics
              .uniqueUsers,
          ],
          [
            "CTR",
            `${ctr.toFixed(
              2,
            )}%`,
          ],
        ].map(
          ([
            label,
            value,
          ]) => (
            <Grid
              item
              xs={12}
              md={3}
              key={
                label
              }
            >
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius:
                    "20px",
                }}
              >
                <Typography
                  color="text.secondary"
                >
                  {label}
                </Typography>

                <Typography
                  variant="h4"
                  fontWeight={900}
                  mt={1}
                >
                  {value}
                </Typography>
              </Paper>
            </Grid>
          ),
        )}
      </Grid>

      <Paper
        sx={{
          p: 3,
          borderRadius:
            "22px",
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={900}
          mb={2}
        >
          الترجمات
        </Typography>

        {(
          [
            [
              "ar",
              "العربية",
            ],
            [
              "en",
              "English",
            ],
            [
              "ku",
              "کوردی",
            ],
          ] as const
        ).map(
          ([
            code,
            label,
          ]) => {
            const value =
              advertisement
                .translations[
                  code
                ];

            if (!value) {
              return null;
            }

            return (
              <Box
                key={
                  code
                }
                mb={2}
              >
                <Typography
                  fontWeight={900}
                >
                  {label}
                </Typography>

                <Typography
                  variant="h6"
                >
                  {
                    value.title
                  }
                </Typography>

                <Typography
                  color="text.secondary"
                >
                  {
                    value.description ??
                    "—"
                  }
                </Typography>

                <Divider
                  sx={{
                    mt: 2,
                  }}
                />
              </Box>
            );
          },
        )}
      </Paper>

      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={6}
        >
          <Paper
            sx={{
              p: 3,
              borderRadius:
                "22px",
              height:
                "100%",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              الاستهداف
            </Typography>

            <Typography mt={2}>
              أنواع الحسابات:{" "}
              {advertisement
                .targeting
                .accountTypes
                .length
                ? advertisement
                    .targeting
                    .accountTypes
                    .join(
                      "، ",
                    )
                : "الجميع"}
            </Typography>

            <Typography mt={1}>
              المستويات:{" "}
              {advertisement
                .targeting
                .accountLevels
                .length
                ? advertisement
                    .targeting
                    .accountLevels
                    .join(
                      "، ",
                    )
                : "الجميع"}
            </Typography>

            <Typography mt={1}>
              اللغات:{" "}
              {advertisement
                .targeting
                .languages
                .length
                ? advertisement
                    .targeting
                    .languages
                    .join(
                      "، ",
                    )
                : "الجميع"}
            </Typography>

            <Typography mt={1}>
              المحافظات:{" "}
              {advertisement
                .targeting
                .provinces
                .length
                ? advertisement
                    .targeting
                    .provinces
                    .join(
                      "، ",
                    )
                : "الجميع"}
            </Typography>

            <Typography mt={1}>
              مستخدمون جدد فقط:{" "}
              {advertisement
                .targeting
                .newUsersOnly
                ? "نعم"
                : "لا"}
            </Typography>
          </Paper>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
        >
          <Paper
            sx={{
              p: 3,
              borderRadius:
                "22px",
              height:
                "100%",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              الجدولة
            </Typography>

            <Typography mt={2}>
              البداية:{" "}
              {formatDate(
                advertisement.startAt,
              )}
            </Typography>

            <Typography mt={1}>
              النهاية:{" "}
              {formatDate(
                advertisement.endAt,
              )}
            </Typography>

            <Typography mt={1}>
              الأولوية:{" "}
              {
                advertisement.priority
              }
            </Typography>

            <Typography mt={1}>
              أماكن الظهور:{" "}
              {advertisement
                .placements
                .join(
                  "، ",
                )}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdvertisementDetails;